import {
  addDoc, arrayRemove, arrayUnion, collection, deleteDoc, doc, getDocs, onSnapshot, query,
  Timestamp, updateDoc, where, writeBatch,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { chunk, docsWithId, logListenerError } from './db';

const opportunitiesCollection = () => collection(db, 'opportunities');
const opportunityRef = (id) => doc(db, 'opportunities', id);

/** Live list of every opportunity, including pending and rejected ones (admin workspace). */
export function subscribeOpportunities(onChange, onError = logListenerError('opportunities')) {
  return onSnapshot(opportunitiesCollection(), (snapshot) => onChange(docsWithId(snapshot)), onError);
}

// Public listings are the approved ones; pending and rejected (mostly scraped) stay admin-only.
const listedQuery = () => query(opportunitiesCollection(), where('status', '==', 'approved'));

/** Live list of approved opportunities (what members see). */
export function subscribeListedOpportunities(onChange, onError = logListenerError('listed opportunities')) {
  return onSnapshot(listedQuery(), (snapshot) => onChange(docsWithId(snapshot)), onError);
}

export async function listListedOpportunities() {
  return docsWithId(await getDocs(listedQuery()));
}

/** Opportunities created by a profile (createdBy). */
export async function listOpportunitiesCreatedBy(profileId) {
  return docsWithId(await getDocs(query(opportunitiesCollection(), where('createdBy', '==', profileId))));
}

/**
 * Queries matching every field older opportunities used to record their owner:
 * createdBy / userId / ownerId / businessId by profile ID, organiser / organizer by name.
 */
function ownerQueries(profileId, organizerName) {
  const queries = [];
  if (profileId) {
    ['createdBy', 'userId', 'ownerId', 'businessId'].forEach((field) => {
      queries.push(query(opportunitiesCollection(), where(field, '==', profileId)));
    });
  }
  if (organizerName) {
    ['organiser', 'organizer'].forEach((field) => {
      queries.push(query(opportunitiesCollection(), where(field, '==', organizerName)));
    });
  }
  return queries;
}

const mergeById = (lists) => {
  const byId = new Map();
  lists.flat().forEach((opportunity) => byId.set(opportunity.id, opportunity));
  return [...byId.values()];
};

/** All opportunities owned by a profile (any owner field), fetched once. */
export async function listOpportunitiesOwnedBy({ profileId, organizerName }) {
  const snapshots = await Promise.all(ownerQueries(profileId, organizerName).map((q) => getDocs(q)));
  return mergeById(snapshots.map(docsWithId));
}

/** Live version of listOpportunitiesOwnedBy. Calls onChange([]) when there is nothing to query. */
export function subscribeOpportunitiesOwnedBy({ profileId, organizerName }, onChange, onError = logListenerError('owned opportunities')) {
  const queries = ownerQueries(profileId, organizerName);
  if (queries.length === 0) {
    onChange([]);
    return () => {};
  }
  const latest = queries.map(() => []);
  const unsubscribers = queries.map((q, index) => onSnapshot(q, (snapshot) => {
    latest[index] = docsWithId(snapshot);
    onChange(mergeById(latest));
  }, onError));
  return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
}

/** Create an opportunity. A Date deadline is stored as YYYY-MM-DD like the scraper's. */
export async function createOpportunity(data) {
  const ref = await addDoc(opportunitiesCollection(), normalizeDeadline(data));
  return ref.id;
}

export function updateOpportunity(id, data) {
  return updateDoc(opportunityRef(id), normalizeDeadline(data));
}

/** Apply the same update to many opportunities (batched writes of 400). */
export async function updateOpportunities(ids, data) {
  for (const group of chunk(ids, 400)) {
    const batch = writeBatch(db);
    group.forEach((id) => batch.update(opportunityRef(id), normalizeDeadline(data)));
    await batch.commit();
  }
}

export function deleteOpportunity(id) {
  return deleteDoc(opportunityRef(id));
}

export function setOpportunitySaved(id, profileId, saved) {
  return updateDoc(opportunityRef(id), { savedBy: saved ? arrayUnion(profileId) : arrayRemove(profileId) });
}

function normalizeDeadline(data) {
  if (data.deadline instanceof Date) return { ...data, deadline: data.deadline.toISOString().slice(0, 10) };
  if (data.deadline instanceof Timestamp) return { ...data, deadline: data.deadline.toDate().toISOString().slice(0, 10) };
  return data;
}

// ─── Applications ───────────────────────────────────────────────────

const applicationsCollection = () => collection(db, 'applications');

/** Whether this profile already applied to this opportunity. */
export async function hasApplied(opportunityId, applicantId) {
  const snapshot = await getDocs(query(applicationsCollection(),
    where('opportunityId', '==', opportunityId), where('applicantId', '==', applicantId)));
  return !snapshot.empty;
}

/** Submit an application. businessId (the opportunity owner) is required by security rules. */
export function applyToOpportunity({ opportunityId, applicantId, applicantName, applicantEmail, businessId, message }) {
  return addDoc(applicationsCollection(), {
    opportunityId, applicantId, applicantName, applicantEmail, businessId, message, status: 'pending', createdAt: Date.now(),
  });
}

/** Applications to a business's opportunities (opportunityIds batched 30 at a time). */
export async function listApplicationsForBusiness(businessId, opportunityIds) {
  const batches = await Promise.all(chunk(opportunityIds, 30).map((ids) => getDocs(query(applicationsCollection(),
    where('businessId', '==', businessId), where('opportunityId', 'in', ids)))));
  return batches.flatMap(docsWithId);
}

/** Number of applications to one of a business's opportunities. */
export async function countApplications(businessId, opportunityId) {
  const snapshot = await getDocs(query(applicationsCollection(),
    where('businessId', '==', businessId), where('opportunityId', '==', opportunityId)));
  return snapshot.size;
}

export function setApplicationStatus(id, status) {
  return updateDoc(doc(db, 'applications', id), { status });
}
